import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useRemindPinStore } from '@lib/store';
import { useAccountContext } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import { MobileHeader, Success } from '@widgets';
import { Button, Spinner, TextInput } from '@shared/widgets';

interface IRemindPinForm {
  password: string;
}
const DEFAULT_VALUES: IRemindPinForm = { password: '' };

export const RemindPin = () => {
  const remindPinStore = useRemindPinStore();

  const { card } = useAccountContext();
  const { control, formState, handleSubmit } = useForm<IRemindPinForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('card');

  useEffect(() => {
    return () => remindPinStore.clear();
  }, []);

  const onSubmit = async (data: IRemindPinForm) => {
    if (!card) return;
    await remindPinStore.remind({ cardId: card.id, password: data.password });
  };

  if (!card?.id) return null;
  return (
    <>
      <MobileHeader />
      <form
        className='p-10 text-base relative flex flex-col flex-nowrap justify-between min-h-[400px] align-end'
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit(onSubmit)}
      >
        {remindPinStore.loading && <Spinner position dark />}
        {remindPinStore.data && <Success text={t('pinReminder.success')} />}
        <div className='text-[26px] mb-5 text-left w-full max-[840px]:text-lg'>{t('pinReminder.title')}</div>
        <div className='flex align-start justify-between border border-[#dadada] p-5 w-full mb-5 max-[840px]:flex-col'>
          <div>
            <p className={'text-base text-left m-0 leading-[34px]'}>{t('pinReminder.description')}</p>
            <div className={clsx('text-[27px] mb-5', PaletteUtils.isBordierPalette ? '!text-[#dc291c]' : 'text-[#c5a466]')}>
              {card?.contactPhoneNumber}
            </div>
          </div>
          <div>
            <Controller
              name='password'
              control={control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.password', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  inputClassName='max-w-[230px] max-[840px]:max-w-full'
                  formStyle
                  caption={t('placeholders.password', { ns: 'common' })}
                  required
                  name='password'
                  type='password'
                  error={error?.message || (remindPinStore.error === 'Wrong password' ? t('errors.form.password.wrong') : undefined)}
                />
              )}
            />
          </div>
        </div>
        <Button
          className='px-4 ml-auto font-semibold tracking-[-0.2px]'
          view='secondary'
          caption={t('pinReminder.buttons.sendPin')}
          type='submit'
          disabled={formState.isSubmitting}
        />
      </form>
    </>
  );
};
